import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import api from "../../lib/api";

// Live, interactive taste of the AI coaches — real replies, IP rate-limited on the backend.
const SUGGESTIONS = [
  "I keep skipping workouts. Help.",
  "How do I start eating better?",
  "I'm tired all the time — what do I do?",
  "How do I stay consistent?",
];

export function CoachPreview({ onJoin }) {
  const [coaches, setCoaches] = useState([]);
  const [active, setActive] = useState(null);
  const [input, setInput] = useState("");
  const [thread, setThread] = useState([]); // {role, text}
  const [busy, setBusy] = useState(false);
  const [limited, setLimited] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.coaches().then((c) => {
      const list = Array.isArray(c) ? c : [];
      setCoaches(list);
      if (list[0]) setActive(list[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [thread, busy]);

  const coach = coaches.find((c) => c.id === active);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy || !active) return;
    setInput("");
    setThread((t) => [...t, { role: "me", text: q }]);
    setBusy(true);
    try {
      const res = await api.coachDemo(active, q);
      setThread((t) => [...t, { role: "coach", text: res.reply }]);
      if (res.remaining === 0) setLimited(true);
    } catch (err) {
      if (err.status === 429) {
        setLimited(true);
        setThread((t) => [...t, { role: "coach", text: "You've reached the free demo limit — start a free 30-day trial and I'll be right here to go deeper with you." }]);
      } else {
        setThread((t) => [...t, { role: "coach", text: "I'm taking a quick breath — try me again in a moment." }]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="try-coach" className="relative mx-auto max-w-4xl px-5 py-20 lg:px-8" data-testid="coach-preview">
      <div className="mb-10 text-center">
        <p className="eyebrow">Try it now</p>
        <h2 className="mt-5 text-3xl font-semibold text-mist sm:text-4xl">Talk to a coach. Right now.</h2>
        <p className="mx-auto mt-4 max-w-xl text-mist-muted">No sign-up needed. Pick a coach, ask anything, and feel the difference. This is the real AI — just a taste.</p>
      </div>

      <div className="glass relative overflow-hidden rounded-[2rem] p-4 sm:p-6">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand/15 blur-[90px]" />
        {/* Coach picker */}
        <div className="relative mb-4 flex flex-wrap gap-2">
          {coaches.map((c) => (
            <button
              key={c.id}
              onClick={() => { setActive(c.id); setThread([]); setLimited(false); }}
              data-testid={`coach-preview-pick-${c.id}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-all ${active === c.id ? "text-[#05101f]" : "border border-white/10 text-mist-muted hover:text-mist"}`}
              style={active === c.id ? { background: c.accent || "#6AA6FF" } : undefined}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Thread */}
        <div ref={scrollRef} data-lenis-prevent className="relative max-h-[300px] min-h-[180px] overflow-y-auto rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="coach-preview-thread">
          {thread.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center py-6 text-center">
              <Sparkles size={22} className="text-brand-soft" />
              <p className="mt-3 text-sm text-mist-muted">
                {coach ? <><span className="font-medium text-mist">{coach.name}</span> — {coach.role || "your coach"} — is ready.</> : "Loading coaches…"}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => ask(s)} data-testid="coach-preview-suggestion"
                    className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-mist-muted transition-colors hover:border-brand/40 hover:text-mist">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {thread.map((m, i) => (
                <div key={i} className={`flex ${m.role === "me" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "me" ? "bg-brand text-[#05101f]" : "bg-white/[0.05] text-mist"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-white/[0.05] px-4 py-2.5 text-sm text-mist-muted"><Loader2 size={15} className="animate-spin" /></div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input */}
        {limited ? (
          <button onClick={onJoin} data-testid="coach-preview-cta" className="btn-primary mt-4 flex w-full items-center justify-center gap-2">
            Start free — keep training <ArrowRight size={16} />
          </button>
        ) : (
          <div className="mt-4 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder={coach ? `Ask ${coach.name} anything…` : "Ask anything…"}
              data-testid="coach-preview-input"
              disabled={busy}
              className="aeva-input flex-1"
            />
            <button onClick={() => ask()} disabled={busy || !input.trim()} data-testid="coach-preview-send"
              className="btn-primary flex h-11 w-11 items-center justify-center !p-0 disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
